import { createInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { i18nResources } from '@/lib/i18n-resources';

export { i18nResources };

const getInitialLanguage = (): string => {
  if (typeof window === 'undefined') return 'pt-BR';
  const stored =
    typeof window.localStorage === 'object' ? window.localStorage?.getItem('lang') : null;
  if (stored) return stored;
  return 'pt-BR';
};

function initializeI18nInstance(language: string, bindAsReactDefault: boolean) {
  const instance = createInstance();
  if (bindAsReactDefault) instance.use(initReactI18next);
  void instance
    .init({
      resources: i18nResources,
      lng: language,
      initAsync: false,
      // This app is pt-BR first. Avoid leaking English strings via fallback when a pt-BR key is missing.
      fallbackLng: 'pt-BR',
      ns: [
        'appShell',
        'ar',
        'auth',
        'closingChecklist',
        'financialControls',
        'consumptionTreatment',
        'contractReview',
        'deferrals',
        'bills',
        'billing',
        'btgConnection',
        'bundles',
        'cashflow',
        'commonComponents',
        'connectedAgents',
        'writeConfirmations',
        'dataio',
        'entities',
        'entityChannels',
        'officialMailboxes',
        'entityObligationResponsibles',
        'entityObligations',
        'obligationsCentral',
        'entityObligationSuggestions',
        'establishmentProfile',
        'expenseAccruals',
        'templates',
        'gl',
        'hcm',
        'payeeAccounts',
        'integrations',
        'intakeRules',
        'noBillEmails',
        'investments',
        'lab',
        'loans',
        'managerDashboard',
        'managerialAccounts',
        'navigation',
        'obligations',
        'documents',
        'emittedNfs',
        'permissions',
        'preferences',
        'procurement',
        'receivedNfs',
        'reconciliation',
        'vendors',
        'vendorKnowledge',
        'approvalWorkflows',
        'reimbursementPolicies',
        'settingsHub',
        'tenantSettings',
        'taxAssessments',
        'creditCards',
      ],
      defaultNS: 'gl',
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
      },
    })
    .catch((err) => {
      console.error('[i18n] Failed to initialize', err);
    });
  return instance;
}

/**
 * Creates a fully independent i18n instance without replacing react-i18next's
 * process-global default. Storybook provides it through I18nextProvider, one
 * per rendered story, so locale changes cannot race another visual-test worker.
 */
export function createI18nInstance(language: string) {
  return initializeI18nInstance(language, false);
}

const i18n = initializeI18nInstance(getInitialLanguage(), true);

// Vite replaces a catalog module without recreating this long-lived i18next instance. An edit to
// any catalog re-evaluates `i18n-resources`, so refresh every namespace from it and a hot edit
// never renders raw translation keys.
if (import.meta.hot) {
  import.meta.hot.accept('@/lib/i18n-resources', (updated) => {
    if (!updated) return;
    const next: typeof i18nResources = updated.i18nResources;
    for (const [language, namespaces] of Object.entries(next)) {
      for (const [namespace, bundle] of Object.entries(namespaces)) {
        i18n.addResourceBundle(language, namespace, bundle, true, true);
      }
    }
  });
}

export default i18n;
